import React, { useMemo } from "react";

import { Area, CartesianGrid, ComposedChart, Label, Line, Tooltip as RechartsTooltip, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from "recharts";

import { useSoilSightCurrency, useTranslation } from "utils";

import {
  CHART_ACTIVE_DOT_RADIUS,
  CHART_AXIS_COLOR,
  CHART_GRID_STROKE,
  CHART_LABEL_FONT_SIZE,
  CHART_LINE_WIDTH,
  CHART_MUTED_COLOR,
  DIFFERENCE_CHART_HEIGHT,
  DIFFERENCE_CHART_MARGIN,
  DIFFERENCE_GAIN_FILL,
  DIFFERENCE_LOSS_FILL,
  PAGE_AXIS_STYLE,
} from "consts";

import { OutcomeTooltip } from "pages/app/scenario-analysis/components/OutcomeTooltip";
import { useDifferenceAmount } from "pages/app/scenario-analysis/hooks/useDifferenceAmount";
import { useSvgId } from "pages/app/scenario-analysis/hooks/useSvgId";
import { useYearLabel } from "pages/app/scenario-analysis/hooks/useYearLabel";
import { leftAlignedYAxisTick, yearXAxisProps } from "pages/app/scenario-analysis/utils/chartAxes";
import { DifferenceDatum, buildDifferenceAxis, finalDifference } from "pages/app/scenario-analysis/utils/differenceView";
import { tourTargetProps } from "pages/app/scenario-analysis/utils/outcomesTourTarget";

type DifferenceChartProps = {
  series: DifferenceDatum[];
  /** Comparison scenario name, for the endpoint label's tooltip row and the legend line at zero. */
  comparisonTitle: string;
  baselineTitle: string;
  /** Stroke of the comparison line; the scenario's own colour, so it matches the key insights picker. */
  lineColor: string;
  dataTestId?: string;
};

type EndpointLabelProps = {
  x?: string | number;
  y?: string | number;
  index?: number;
};

type DifferenceTooltipProps = {
  active?: boolean;
  payload?: ReadonlyArray<{ payload?: DifferenceDatum }>;
};

export const DifferenceChart = ({ series, comparisonTitle, baselineTitle, lineColor, dataTestId = "difference-chart" }: DifferenceChartProps) => {
  const { t } = useTranslation("scenarioAnalysis");
  const { formatCmOutcome } = useSoilSightCurrency();
  const differenceAmount = useDifferenceAmount();

  const gradientId = useSvgId("difference-area");

  const axis = useMemo(() => buildDifferenceAxis(series.map((row) => row.difference)), [series]);

  // Ticks are whole rounding steps, so the house rounding never changes them; only data figures need exact values.
  const signedTick = (value: number) => formatCmOutcome(value, { signDisplay: "exceptZero" });
  const endpointLabel = t("outcomeComparison.difference.endpointTotal", { value: differenceAmount(finalDifference(series)) });
  const lastIndex = series.length - 1;

  const { formatYearShort: yearLabel } = useYearLabel();

  const renderTooltip = ({ active, payload }: DifferenceTooltipProps) => {
    const row = payload?.[0]?.payload;
    if (!active || !row) return null;

    // Wording follows the raw gap, the same value the shading and the Breaks even card judge, so a year
    // is never described as ahead while the chart shades it as a loss.
    const gap = row.difference;
    const gapLabel =
      gap >= 0
        ? t("outcomeComparison.difference.tooltipAhead", { value: differenceAmount(gap) })
        : t("outcomeComparison.difference.tooltipBehind", { value: differenceAmount(gap) });

    return (
      <OutcomeTooltip
        header={yearLabel(row.yearNum)}
        rows={[
          { key: "baseline", color: CHART_MUTED_COLOR, name: baselineTitle, value: formatCmOutcome(0) },
          { key: "comparison", color: lineColor, name: comparisonTitle, value: gapLabel },
        ]}
      />
    );
  };

  // Right-anchored at the endpoint, so the label grows leftwards into the plot and no label length can
  // reach the right edge; the margin below only has to clear the small overhang past the last point.
  const renderEndpointLabel = ({ x, y, index }: EndpointLabelProps) => {
    if (index !== lastIndex || x === undefined || y === undefined) return null;
    return (
      <text
        x={Number(x) + 6}
        y={Number(y) - 14}
        textAnchor="end"
        fontSize={CHART_LABEL_FONT_SIZE}
        fill={CHART_AXIS_COLOR}
        data-testid={`${dataTestId}-endpoint-total`}
      >
        {endpointLabel}
      </text>
    );
  };

  return (
    <div className="outcome-comparison-chart" data-testid={dataTestId} {...tourTargetProps("chart")}>
      <div className="outcome-comparison-chart-canvas">
        <ResponsiveContainer width="100%" height={DIFFERENCE_CHART_HEIGHT}>
          <ComposedChart data={series} margin={DIFFERENCE_CHART_MARGIN}>
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset={0} stopColor={DIFFERENCE_GAIN_FILL} />
                <stop offset={axis.zeroOffset} stopColor={DIFFERENCE_GAIN_FILL} />
                <stop offset={axis.zeroOffset} stopColor={DIFFERENCE_LOSS_FILL} />
                <stop offset={1} stopColor={DIFFERENCE_LOSS_FILL} />
              </linearGradient>
            </defs>

            <CartesianGrid vertical={false} stroke={CHART_GRID_STROKE} />
            <XAxis dataKey="yearNum" {...yearXAxisProps(PAGE_AXIS_STYLE)} tickFormatter={(value: number) => yearLabel(value)} />
            <YAxis
              domain={axis.domain}
              ticks={axis.ticks}
              tick={leftAlignedYAxisTick(PAGE_AXIS_STYLE, signedTick)}
              tickLine={false}
              axisLine={false}
              width={64}
            />
            <RechartsTooltip content={renderTooltip} />

            {/* Shading only — the stroke is the Line below, so the gradient never tints the line itself. */}
            <Area type="monotone" dataKey="difference" baseValue={0} stroke="none" fill={`url(#${gradientId})`} isAnimationActive={false} activeDot={false} />

            <ReferenceLine y={0} stroke={CHART_MUTED_COLOR} strokeWidth={1.5} ifOverflow="extendDomain">
              <Label
                value={t("outcomeComparison.difference.baselineLine")}
                position="insideBottomRight"
                fontSize={CHART_LABEL_FONT_SIZE}
                fill={CHART_MUTED_COLOR}
              />
            </ReferenceLine>

            <Line
              type="monotone"
              dataKey="difference"
              name={comparisonTitle}
              stroke={lineColor}
              strokeWidth={CHART_LINE_WIDTH}
              dot={{ r: 3, fill: lineColor, strokeWidth: 0 }}
              activeDot={{ r: CHART_ACTIVE_DOT_RADIUS, fill: lineColor }}
              isAnimationActive={false}
              label={renderEndpointLabel}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default DifferenceChart;
